import React, { useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const card = { background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', boxShadow: '0 1px 4px rgba(15,23,42,0.05)' };
const muted = { color: '#64748b', fontSize: '12px' };
const colors = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#65a30d', '#ea580c', '#475569'];

function euro(v) {
  return Number(v || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

function pct(v) {
  const n = Number(v || 0);
  return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
}

export default function CockpitPage() {
  const nav = useNavigate();
  const [months, setMonths] = useState('6');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nextPayments, setNextPayments] = useState([]);
  const [catBudgets, setCatBudgets] = useState(null);

  useEffect(() => { load(); }, [months]);
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const to = new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10);
    api.get('/forecast/payments', { params: { from: today, to, include_low_confidence: false } })
      .then(r => setNextPayments((r.data.items || []).filter(i => !i.alreadyBooked).slice(0, 6)))
      .catch(() => {});
  }, []);
  useEffect(() => {
    const now = new Date();
    const y = now.getFullYear(), m = String(now.getMonth() + 1).padStart(2, '0');
    const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
    const from = `${y}-${m}-01`, to = `${y}-${m}-${String(lastDay).padStart(2, '0')}`;
    api.get('/budgets/categories', { params: { from, to } })
      .then(r => setCatBudgets(r.data))
      .catch(() => {});
  }, []);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const r = await api.get('/cockpit', { params: { monate: months } });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Cockpit konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }

  const pieData = useMemo(() => (data?.kategorien || []).slice(0, 8), [data]);
  const trendTone = (data?.summary?.ausgabenTrend || 0) > 0 ? '#dc2626' : '#16a34a';
  const reviewInbox = data?.reviewInbox || {};

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '14px' }}>
        <div>
          <h1 style={{ margin: 0, color: '#0f172a', fontSize: '23px' }}>Finanz-Cockpit</h1>
          <p style={{ ...muted, margin: '4px 0 0' }}>Trends, Fixkosten, Warnungen und Veraenderungen auf einen Blick.</p>
        </div>
        <select value={months} onChange={e => setMonths(e.target.value)} style={{ padding: '9px', borderRadius: '9px', border: '1px solid #cbd5e1', background: 'white' }}>
          <option value="3">3 Monate</option>
          <option value="6">6 Monate</option>
          <option value="12">12 Monate</option>
          <option value="24">24 Monate</option>
        </select>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: '10px', padding: '10px 12px', marginBottom: '12px', fontWeight: 700 }}>{error}</div>}
      {loading && <div style={card}>Cockpit wird geladen...</div>}

      {!loading && data && (
        <>
          <section style={{ ...card, borderColor: (reviewInbox.totals?.hoch || 0) ? '#fecaca' : '#d8e4dd', background: (reviewInbox.totals?.hoch || 0) ? '#fff7f5' : '#f8fffb', marginBottom: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', alignItems: 'center' }}>
              <div>
                <h2 style={{ margin: '0 0 4px', color: (reviewInbox.totals?.hoch || 0) ? '#7f1d1d' : '#064734', fontSize: '16px' }}>Finanz-Review</h2>
                <div style={muted}>{reviewInbox.totals?.gesamt || 0} offene Prüfpunkte, davon {reviewInbox.totals?.hoch || 0} dringend.</div>
              </div>
              <button onClick={() => nav('/review')} style={smallButton('#064734')}>Review-Inbox öffnen</button>
            </div>
          </section>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '10px', marginBottom: '12px' }}>
            <Stat title="Ausgaben" value={euro(data.summary.ausgaben)} hint={`${pct(data.summary.ausgabenTrend)} zum Vormonat`} color={trendTone} />
            <Stat title="Einnahmen" value={euro(data.summary.einnahmen)} hint={data.zeitraum.latestMonth} color="#16a34a" />
            <Stat title="Saldo" value={euro(data.summary.saldo)} hint={`Vormonat ${euro(data.summary.saldoVormonat)}`} color={data.summary.saldo >= 0 ? '#16a34a' : '#dc2626'} />
            <Stat title="Fixkosten/Monat" value={euro(data.summary.fixkostenMonat)} hint={`${euro(data.summary.variabel)} variabel`} color="#2563eb" />
          </div>

          {catBudgets && (() => {
            const allCats = catBudgets.categories || [];
            const critCats = allCats.filter(c => c.warning_level === 'over' || c.warning_level === 'critical');
            const hasBudget = allCats.filter(c => c.budget !== null).length;
            if (!hasBudget) return null;
            const isAlert = critCats.length > 0;
            return (
              <section style={{ ...card, borderColor: isAlert ? '#fecaca' : '#d8e4dd', background: isAlert ? '#fff7f5' : '#f8fffb', marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div>
                    <h2 style={{ margin: '0 0 4px', color: isAlert ? '#7f1d1d' : '#064734', fontSize: '16px' }}>Kategorie-Budgets</h2>
                    <div style={muted}>{hasBudget} Kategorien mit Budget · {critCats.length} kritisch/überschritten</div>
                  </div>
                  <button onClick={() => nav('/budgets')} style={smallButton(isAlert ? '#b42318' : '#0a6f4f')}>Kategorie-Budgets</button>
                </div>
                {critCats.length === 0 && (
                  <div style={{ ...muted, color: '#0a6f4f' }}>Alle Kategorie-Budgets im grünen Bereich.</div>
                )}
                {critCats.slice(0, 5).map(c => (
                  <div key={c.category} style={{ borderTop: '1px solid #fecaca', padding: '8px 0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                      <strong style={{ color: c.warning_level === 'over' ? '#b42318' : '#a16207', fontSize: '13px' }}>{c.category}</strong>
                      <strong style={{ color: c.warning_level === 'over' ? '#b42318' : '#a16207' }}>{c.projected_pct}%</strong>
                    </div>
                    <div style={muted}>{Number(c.spent||0).toLocaleString('de-DE',{style:'currency',currency:'EUR'})} Ist + {Number(c.planned||0).toLocaleString('de-DE',{style:'currency',currency:'EUR'})} geplant von {Number(c.budget||0).toLocaleString('de-DE',{style:'currency',currency:'EUR'})} Budget · {c.warning_level === 'over' ? 'Überschritten' : 'Kritisch'}</div>
                  </div>
                ))}
              </section>
            );
          })()}

          {data.warnings.length > 0 && (
            <div style={{ ...card, background: '#fffbeb', borderColor: '#fbbf24', marginBottom: '12px' }}>
              <h2 style={{ margin: '0 0 8px', color: '#92400e', fontSize: '15px' }}>Budgetwarnungen</h2>
              {data.warnings.map(w => <div key={w.titel} style={{ color: '#92400e', fontSize: '13px', fontWeight: 700, padding: '4px 0' }}>{w.titel}: {w.text}</div>)}
            </div>
          )}

          {data.budgetStatus && (
            <section style={{ ...card, borderColor: data.budgetStatus.totals.red ? '#fecaca' : '#d8e4dd', background: data.budgetStatus.totals.red ? '#fff7f5' : '#f8fffb', marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div>
                  <h2 style={{ margin: '0 0 4px', color: data.budgetStatus.totals.red ? '#7f1d1d' : '#064734', fontSize: '16px' }}>Budget-Ampeln</h2>
                  <div style={muted}>{data.budgetStatus.activeItems.length} aktive Budgets · {euro(data.budgetStatus.totals.ausgaben)} von {euro(data.budgetStatus.totals.limit)} genutzt</div>
                </div>
                <button onClick={() => nav('/budgets')} style={smallButton(data.budgetStatus.activeItems.length ? '#0a6f4f' : '#334155')}>Budgets bearbeiten</button>
              </div>
              {data.budgetStatus.activeItems.length === 0 && <div style={muted}>Noch keine Budgets gesetzt. Lege Monatslimits an, dann erscheinen hier Ampeln.</div>}
              {data.budgetStatus.activeItems.slice(0, 6).map(item => (
                <div key={item.id} style={{ borderTop: '1px solid #eef4f1', padding: '8px 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                    <strong style={{ color: item.tone === 'red' ? '#b42318' : item.tone === 'amber' ? '#a16207' : '#064734', fontSize: '13px' }}>{item.kategorie}</strong>
                    <strong style={{ color: item.tone === 'red' ? '#b42318' : item.tone === 'amber' ? '#a16207' : '#064734' }}>{item.prozent}%</strong>
                  </div>
                  <div style={muted}>{euro(item.ausgaben)} von {euro(item.monatliches_limit)} · {item.status}</div>
                </div>
              ))}
            </section>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 0.9fr', gap: '12px', marginBottom: '12px' }}>
            <Section title="Monatsentwicklung">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.monatlich}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="monat" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={v => euro(v)} />
                  <Bar dataKey="ausgaben" fill="#dc2626" name="Ausgaben" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="einnahmen" fill="#16a34a" name="Einnahmen" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Section>

            <Section title="Kategorien aktuell">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} dataKey="gesamt" nameKey="name" outerRadius={86}>
                    {pieData.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
                  </Pie>
                  <Tooltip formatter={v => euro(v)} />
                </PieChart>
              </ResponsiveContainer>
            </Section>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <Section title="Was hat sich verändert?">
              {(data.veraenderungen || []).slice(0, 8).map(v => (
                <Row key={v.kategorie} left={v.kategorie} sub={`${euro(v.vorher)} -> ${euro(v.aktuell)}`} right={`${v.differenz >= 0 ? '+' : ''}${euro(v.differenz)}`} tone={v.differenz > 0 ? '#dc2626' : '#16a34a'} />
              ))}
            </Section>

            <Section title="Fixkosten-Radar">
              {(data.fixkosten || []).slice(0, 8).map(f => (
                <Row key={f.empfaenger} left={f.empfaenger} sub={`${f.kategorie || 'ohne Kategorie'} ?? ${f.monate} Monate`} right={euro(f.durchschnitt)} tone="#2563eb" />
              ))}
              {data.fixkosten.length === 0 && <div style={muted}>Noch keine wiederkehrenden Zahlungen erkannt.</div>}
            </Section>
          </div>

          {data.anomalyRadar && (
            <section style={{ ...card, borderColor: data.anomalyRadar.totals.hoch ? '#fecaca' : '#d8e4dd', background: data.anomalyRadar.totals.hoch ? '#fff7f5' : '#f8fffb', marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div>
                  <h2 style={{ margin: '0 0 4px', color: data.anomalyRadar.totals.hoch ? '#7f1d1d' : '#064734', fontSize: '16px' }}>Anomalie-Radar</h2>
                  <div style={muted}>{data.anomalyRadar.totals.gesamt || 0} Auffälligkeiten · {data.anomalyRadar.totals.hoch || 0} hoch</div>
                </div>
                <button onClick={() => nav('/anomalies')} style={smallButton('#b42318')}>Anomalien prüfen</button>
              </div>
              {(data.anomalyRadar.anomalies || []).slice(0, 4).map((item, idx) => (
                <button key={`${item.typ}-${idx}`} onClick={() => nav(item.detail_url)} style={{ width: '100%', background: 'white', border: '1px solid #eef4f1', borderRadius: '9px', padding: '9px', marginTop: '7px', textAlign: 'left', cursor: 'pointer' }}>
                  <strong style={{ color: item.prioritaet === 'hoch' ? '#b42318' : '#12231d', fontSize: '13px' }}>{item.titel}</strong>
                  <div style={{ ...muted, marginTop: '3px' }}>{item.typ} · {item.text}</div>
                </button>
              ))}
            </section>
          )}

          {data.contractRadar && (
            <section style={{ ...card, borderColor: data.contractRadar.totals.kuendigen ? '#fecaca' : '#d8e4dd', background: data.contractRadar.totals.kuendigen ? '#fff7f5' : '#f8fffb', marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div>
                  <h2 style={{ margin: '0 0 4px', color: data.contractRadar.totals.kuendigen ? '#7f1d1d' : '#064734', fontSize: '16px' }}>Abo- und Vertragsradar</h2>
                  <div style={muted}>{data.contractRadar.items.length} wiederkehrende Zahlungen · {euro(data.contractRadar.totals.jahreswert)} Jahreswert</div>
                </div>
                <button onClick={() => nav('/contracts')} style={smallButton('#0a6f4f')}>Radar öffnen</button>
              </div>
              {data.contractRadar.items.slice(0, 5).map(item => (
                <button key={`${item.empfaenger}-${item.kategorie}`} onClick={() => nav(item.detail_url)} style={{ width: '100%', background: 'white', border: '1px solid #eef4f1', borderRadius: '9px', padding: '9px', marginTop: '7px', textAlign: 'left', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                    <strong style={{ color: '#12231d', fontSize: '13px' }}>{item.empfaenger}</strong>
                    <strong style={{ color: item.status === 'kuendigen' ? '#dc2626' : '#0a6f4f' }}>{item.status}</strong>
                  </div>
                  <div style={{ ...muted, marginTop: '3px' }}>{item.kategorie} · nächst: {item.naechste_erwartung} · {euro(item.durchschnitt)}</div>
                </button>
              ))}
            </section>
          )}

          {data.geldfresserPlan && (
            <section style={{ ...card, borderColor: '#fecaca', background: '#fff7f5', marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div>
                  <h2 style={{ margin: '0 0 4px', color: '#7f1d1d', fontSize: '16px' }}>Geldfresser-Aktionsplan</h2>
                  <div style={{ ...muted, color: '#9f1239' }}>{data.geldfresserPlan.items.length} bestätigte Kandidaten · {euro(data.geldfresserPlan.totals.jahrespotenzial)} Jahrespotenzial</div>
                </div>
                <button onClick={() => nav('/geldfresser')} style={smallButton('#dc2626')}>Prüfung öffnen</button>
              </div>
              {data.geldfresserPlan.items.length === 0 && <div style={muted}>Noch keine bestätigten Geldfresser. In der Prüfung per Checkbox markieren.</div>}
              {data.geldfresserPlan.items.slice(0, 5).map(item => (
                <button key={`${item.empfaenger}-${item.kategorie}`} onClick={() => nav(item.detail_url)} style={{ width: '100%', background: 'white', border: '1px solid #fee2e2', borderRadius: '9px', padding: '9px', marginTop: '7px', textAlign: 'left', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                    <strong style={{ color: '#7f1d1d', fontSize: '13px' }}>{item.empfaenger}</strong>
                    <strong style={{ color: '#dc2626' }}>{euro(item.jahrespotenzial)}</strong>
                  </div>
                  <div style={{ ...muted, marginTop: '3px' }}>{item.kategorie} · {item.aktion} · {euro(item.monatliches_potenzial)} mtl.</div>
                </button>
              ))}
            </section>
          )}

          {nextPayments.length > 0 && (
            <section style={{ ...card, marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h2 style={{ margin: 0, fontSize: '16px', color: '#0f172a' }}>Nächste Abbuchungen</h2>
                <button onClick={() => nav('/zahlungen')} style={smallButton('#2563eb')}>Vorschau öffnen</button>
              </div>
              {nextPayments.map((p, i) => {
                const [y, m, d] = p.datum.split('-');
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', padding: '7px 0', borderTop: '1px solid #f1f5f9', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: 700, fontSize: '12px', color: '#2563eb', marginRight: 8 }}>{d}.{m}.</span>
                      <span style={{ color: '#0f172a', fontSize: '13px', fontWeight: 600 }}>{p.bezeichnung || p.empfaenger}</span>
                      {p.isVariable && <span style={{ marginLeft: 5, fontSize: 10, color: '#a16207', background: '#fef9c3', padding: '1px 5px', borderRadius: 4 }}>var</span>}
                    </div>
                    <span style={{ color: '#ef4444', fontWeight: 700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{euro(p.erwarteter_betrag)}</span>
                  </div>
                );
              })}
            </section>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Section title="Top-Empfänger">
              {(data.topEmpfaenger || []).slice(0, 10).map(e => (
                <Row key={e.empfaenger} left={e.empfaenger} sub={`${e.kategorie || 'ohne Kategorie'} ?? ${e.anzahl} Buchungen`} right={euro(e.gesamt)} tone="#0f172a" />
              ))}
            </Section>

            <Section title="Markierte Risiken">
              <Risk label="Geldfresser" value={data.flags.geldfresser} color="#f59e0b" />
              <Risk label="Überflüssig" value={data.flags.ueberfluessig} color="#7c3aed" />
              <Risk label="Kündigen" value={data.flags.kuendigen} color="#dc2626" />
            </Section>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ title, value, hint, color }) {
  return <div style={card}><div style={{ ...muted, fontWeight: 800, textTransform: 'uppercase' }}>{title}</div><div style={{ color, fontWeight: 950, fontSize: '22px', marginTop: '5px' }}>{value}</div><div style={{ ...muted, marginTop: '4px' }}>{hint}</div></div>;
}

function Section({ title, children }) {
  return <div style={card}><h2 style={{ margin: '0 0 10px', color: '#0f172a', fontSize: '16px' }}>{title}</h2>{children}</div>;
}

function Row({ left, sub, right, tone }) {
  return <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', padding: '8px 0', borderTop: '1px solid #f1f5f9' }}><div><div style={{ color: '#0f172a', fontWeight: 800, fontSize: '13px' }}>{left}</div><div style={muted}>{sub}</div></div><div style={{ color: tone, fontWeight: 950, whiteSpace: 'nowrap' }}>{right}</div></div>;
}

function Risk({ label, value, color }) {
  return <div style={{ background: '#f8fafc', borderRadius: '9px', padding: '10px', marginBottom: '8px' }}><div style={{ color: '#334155', fontWeight: 800 }}>{label}</div><div style={{ color, fontSize: '21px', fontWeight: 950, marginTop: '3px' }}>{euro(value)}</div></div>;
}

function smallButton(bg) {
  return { background: bg, color: 'white', border: 'none', borderRadius: '9px', padding: '8px 10px', cursor: 'pointer', fontWeight: 900, fontSize: '12px' };
}
